import { ArrowRight } from 'lucide-react';

import { useUnifiedThemeTable } from './use-unified-theme-table';

import { METRIC, type ThemeStatisticData } from '@/entities/insight';
import { Badge } from '@/shared/components/shadcn/badge';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { ToggleStage } from '@/shared/components/stage/toggle-stage';
import { TableSkeleton } from '@/shared/components/table-skeleton/table-skeleton';
import { cn } from '@/shared/utils/cn';

interface Props {
  startDate: string;
  endDate: string;
  queryId: string;
  onNavigateToConversation: () => void;
}

const PAGE_SIZE = 5;

export function UnifiedThemeTable({
  startDate,
  endDate,
  queryId,
  onNavigateToConversation,
}: Props) {
  const { topThemes, metric, setMetric, handleRowClick, isLoading } = useUnifiedThemeTable({
    queryId,
    startDate,
    endDate,
  });

  return (
    <div className="flex flex-col gap-6 rounded-lg border shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-6 pb-0">
        <div className="flex items-center gap-3">
          <p className="text-base-card-foreground text-base font-semibold">Top themes</p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={metric} onValueChange={(value) => setMetric(value as METRIC)}>
            <TabsList>
              <TabsTrigger value={METRIC.MENTION}>{METRIC.MENTION}</TabsTrigger>
              <TabsTrigger value={METRIC.POST}>{METRIC.POST}</TabsTrigger>
            </TabsList>
          </Tabs>
          <button
            className="hover:bg-muted flex size-10 items-center justify-center rounded-md"
            onClick={onNavigateToConversation}
          >
            <ArrowRight className="text-base-foreground size-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <ToggleStage
        status={!isLoading}
        fallback={
          <div className="p-6 py-0">
            <TableSkeleton
              columns={[
                { className: 'w-[40px]' },
                { className: 'flex-1' },
                { className: 'w-[128px]' },
                { className: 'w-[128px]' },
              ]}
              rows={PAGE_SIZE}
              rowHeight="h-[72px]"
            />
          </div>
        }
      >
        <div className="p-6 py-0">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[60px]" />
              <col />
              <col className="w-[140px]" />
              <col className="w-[140px]" />
            </colgroup>
            <thead>
              <tr className="border-base-border border-b">
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  #
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  Theme
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  Sentiment
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase">
                  {metric === METRIC.MENTION ? 'Mention' : 'Post volume'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-base-border divide-y">
              {topThemes.map((theme, index) => (
                <ThemeTableRow
                  key={theme.name}
                  theme={theme}
                  index={index + 1}
                  metric={metric}
                  onClick={handleRowClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      </ToggleStage>
    </div>
  );
}

interface ThemeTableRowProps {
  theme: ThemeStatisticData;
  index: number;
  metric: METRIC;
  onClick: (theme: ThemeStatisticData, event: React.MouseEvent) => void;
}

function ThemeTableRow({ theme, index, metric, onClick }: ThemeTableRowProps) {
  const volume = metric === METRIC.MENTION ? theme.mention_volume : theme.post_volume;
  const isPositive = theme.sentiment === 'positive';
  const isNegative = theme.sentiment === 'negative';

  return (
    <tr
      className="hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={(e) => onClick(theme, e)}
      onContextMenu={(e) => {
        e.preventDefault();
        onClick(theme, e);
      }}
    >
      <td className="text-muted-foreground px-4 py-5 text-sm">{index}</td>
      <td className="text-card-foreground px-4 py-5 text-sm font-medium">{theme.name}</td>
      <td className="px-4 py-5">
        <Badge
          className={cn(
            'text-white',
            isPositive && 'bg-chart-1',
            isNegative && 'bg-base-destructive',
            !isPositive && !isNegative && 'bg-gray-500',
          )}
        >
          {theme.sentiment === 'positive'
            ? 'Positive'
            : theme.sentiment === 'negative'
              ? 'Negative'
              : 'Neutral'}
        </Badge>
      </td>
      <td className="text-card-foreground px-4 py-5 text-sm">{volume.toLocaleString()}</td>
    </tr>
  );
}
